import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { useAuthStore } from '../../stores/authStore';
import { useScheduleForDate } from '../../hooks/useSchedule';
import { ServiceLogButton } from '../service-log';
import { getPendingCount } from '../../services/offline-queue';
import { setupAutoSync, isOnline } from '../../services/sync-service';
import type { Client, PickupItem } from '../../lib/api';
import { getTodayPacific, addDays } from '../../lib/datetime';
import { ClockStatusBar } from '../../modules/time/components/ClockStatusBar';
import { MorningClockPrompt } from '../../modules/time/components/MorningClockPrompt';
import { EndOfDayNudge } from '../../modules/time/components/EndOfDayNudge';
import { useMyTimesheet, type WorkSegment } from '../../hooks/useTimesheets';

// Helper to get next N days for look-ahead (in Pacific timezone)
function getNextDays(count: number): Array<{ date: string; label: string; isToday: boolean }> {
  const days: Array<{ date: string; label: string; isToday: boolean }> = [];
  const todayStr = getTodayPacific();

  for (let i = 0; i < count; i++) {
    const dateStr = addDays(todayStr, i);
    const isToday = i === 0;

    let label: string;
    if (i === 0) {
      label = 'Today';
    } else if (i === 1) {
      label = 'Tomorrow';
    } else {
      // Parse and format the date for display
      const d = new Date(dateStr + 'T12:00:00');
      label = d.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short', month: 'short', day: 'numeric' });
    }

    days.push({ date: dateStr, label, isToday });
  }

  return days;
}

export function TechDashboard() {
  const { user } = useAuthStore();
  const [loggedClients, setLoggedClients] = useState<Set<string>>(new Set());
  const [pendingCount, setPendingCount] = useState(0);
  const [online, setOnline] = useState(isOnline());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(() => getTodayPacific());
  // Get current hour in Pacific timezone for greeting
  const currentHour = parseInt(new Date().toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    hour: 'numeric',
    hour12: false
  }), 10);

  // Get next 7 days for look-ahead
  const lookAheadDays = useMemo(() => getNextDays(7), []);
  const selectedDayInfo = lookAheadDays.find(d => d.date === selectedDate);

  // Set up auto-sync and track pending logs
  useEffect(() => {
    const updatePendingCount = async () => {
      const count = await getPendingCount();
      setPendingCount(count);
    };

    // Initial count
    updatePendingCount();

    // Set up auto-sync
    const cleanupSync = setupAutoSync(() => {
      updatePendingCount();
    });

    // Track online status
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      cleanupSync();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Track last service log timestamp for 30-min nudge (must be declared before handleLogComplete)
  const [lastLogTimestamp, setLastLogTimestamp] = useState<string>('');

  // Mark a client as logged
  const handleLogComplete = (clientId: string) => {
    setLoggedClients(prev => new Set([...prev, clientId]));
    setLastLogTimestamp(new Date().toISOString());
  };

  // Fetch schedule data for selected date
  const { data: scheduleData, isLoading, error } = useScheduleForDate(user?.email, selectedDate);

  // Clock status for EndOfDayNudge
  const todayPacific = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
  const { data: timeRecord } = useMyTimesheet(todayPacific);
  const segments = (timeRecord?.segments || []) as WorkSegment[];
  const isClockedIn = segments.some((s: WorkSegment) => s.clockOut === null);

  // Initialize from API data on mount (clients that have been logged today)
  useEffect(() => {
    if (!scheduleData?.clients) return;
    let latest = '';
    for (const client of scheduleData.clients) {
      const logDate = (client as any).lastServiceLogDate || (client as any).loggedAt || '';
      if (logDate && logDate > latest) latest = logDate;
    }
    if (latest && !lastLogTimestamp) setLastLogTimestamp(latest);
  }, [scheduleData?.clients, lastLogTimestamp]);

  // Compute minutes since last log
  const [minutesSinceLastLog, setMinutesSinceLastLog] = useState<number | null>(null);
  useEffect(() => {
    if (!isClockedIn || !lastLogTimestamp) { setMinutesSinceLastLog(null); return; }
    const check = () => {
      const mins = Math.round((Date.now() - new Date(lastLogTimestamp).getTime()) / 60000);
      setMinutesSinceLastLog(mins);
    };
    check();
    const interval = setInterval(check, 5 * 60000);
    return () => clearInterval(interval);
  }, [isClockedIn, lastLogTimestamp]);

  // Initialize loggedClients from API response (survives page reload)
  useEffect(() => {
    if (!scheduleData?.clients) return;
    const alreadyLogged = new Set<string>();
    for (const client of scheduleData.clients) {
      if ((client as any).logged || (client as any).hasServiceLogToday || (client as any).serviceLogCount > 0) {
        alreadyLogged.add(client.id || (client as any).clientId || '');
      }
    }
    if (alreadyLogged.size > 0) {
      setLoggedClients(prev => {
        const merged = new Set([...prev, ...alreadyLogged]);
        return merged.size > prev.size ? merged : prev;
      });
    }
  }, [scheduleData?.clients]);

  const getGreeting = () => {
    if (currentHour < 12) return 'Good morning';
    if (currentHour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = (user?.name?.split(' ')[0] || 'there').slice(0, 50);

  // Transform clients to schedule stops
  const stops = scheduleData?.clients?.map((client: Client, index: number) => ({
    id: client.id,
    clientName: client.clientName,
    address: formatAddress(client),
    stopOrder: client.stopOrder ?? index + 1,
    notes: client.servicePreferences || client.accessNotes,
    services: client.services ?? [],
    duration: client.stopDuration ?? 60,
    // WO indicators
    openWorkorderCount: client.openWorkorderCount ?? 0,
    hasUrgentWorkorder: client.hasUrgentWorkorder ?? false,
    pickupCount: client.pickupCount ?? 0,
  })) ?? [];

  // Sort by stop order
  stops.sort((a, b) => a.stopOrder - b.stopOrder);


  return (
    <div className="min-h-screen bg-gradient-to-b from-sage-50 to-white">
      {/* Header */}
      <header className="bg-forest-800 text-white">
        <div className="px-4 py-6 safe-top">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-forest-200 text-sm">{getGreeting()}</p>
              <h1 className="text-2xl font-display font-semibold">{firstName}</h1>
            </div>
            <div className="w-12 h-12 rounded-full bg-forest-600 flex items-center justify-center text-lg font-medium">
              {user?.name?.charAt(0) || 'T'}
            </div>
          </div>

          {/* Date Picker */}
          <div className="relative mb-4">
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="w-full flex items-center justify-between bg-forest-700/50 rounded-xl px-4 py-3 hover:bg-forest-700 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">📅</span>
                <span className="font-medium">
                  {selectedDayInfo?.label || 'Select Date'}
                </span>
                {!selectedDayInfo?.isToday && (
                  <Badge variant="info" size="sm">Look-ahead</Badge>
                )}
              </div>
              <span className="text-forest-300">{showDatePicker ? '▲' : '▼'}</span>
            </button>

            {/* Date dropdown */}
            {showDatePicker && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg z-10 overflow-hidden border border-sage-200">
                {lookAheadDays.map((day) => (
                  <button
                    key={day.date}
                    onClick={() => {
                      setSelectedDate(day.date);
                      setShowDatePicker(false);
                    }}
                    className={`w-full px-4 py-3 text-left flex items-center justify-between hover:bg-sage-50 transition-colors ${
                      day.date === selectedDate ? 'bg-primary-50 text-forest-800 font-medium' : 'text-sage-700'
                    }`}
                  >
                    <span>{day.label}</span>
                    <span className="text-sm text-sage-500">{day.date}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Route summary card */}
          <div className="bg-forest-700/50 rounded-2xl p-4 backdrop-blur">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-forest-200 text-sm">
                  {selectedDayInfo?.isToday ? "Today's Route" : `${selectedDayInfo?.label}'s Route`}
                </p>
                <p className="text-xl font-semibold">
                  {scheduleData?.schedule?.routeCode || 'No Route'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-forest-200 text-sm">Total Stops</p>
                <p className="text-xl font-semibold">{stops.length}</p>
              </div>
              <div className="text-right">
                <p className="text-forest-200 text-sm">Logged</p>
                <p className="text-xl font-semibold">{loggedClients.size}</p>
              </div>
            </div>
            {/* Sync status indicator */}
            {(pendingCount > 0 || !online) && (
              <div className="mt-3 flex items-center gap-2 text-sm">
                {!online && (
                  <span className="inline-flex items-center gap-1 text-earth-300">
                    <span className="w-2 h-2 rounded-full bg-earth-400"></span>
                    Offline
                  </span>
                )}
                {pendingCount > 0 && (
                  <span className="inline-flex items-center gap-1 text-forest-200">
                    <span className="w-2 h-2 rounded-full bg-forest-300 animate-pulse"></span>
                    {pendingCount} pending sync
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="px-4 py-6 space-y-4">
        {/* Clock Status Bar - prominent clock-in/out widget */}
        <ClockStatusBar />

        {/* End of day nudges (below clock bar, above route list) */}
        <EndOfDayNudge
          allStopsDone={(scheduleData?.clients?.length ?? 0) > 0 && loggedClients.size >= (scheduleData?.clients?.length ?? 0)}
          minutesSinceLastLog={minutesSinceLastLog}
          isClockedIn={isClockedIn}
        />

        {/* Loading state */}
        {isLoading && (
          <Card variant="elevated" className="animate-pulse">
            <CardContent>
              <div className="h-4 bg-sage-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-sage-200 rounded w-1/2"></div>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {error && (
          <Card variant="elevated" className="border-l-4 border-l-earth-500">
            <CardContent>
              <p className="text-earth-700">Failed to load schedule. Please try again.</p>
              <p className="text-sm text-sage-600 mt-1">
                {error instanceof Error ? error.message : 'Unknown error'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Scheduler Notes Banner */}
        {scheduleData?.schedule?.notes && (
          <SchedulerNotesBanner notes={scheduleData.schedule.notes} />
        )}

        {/* Ready for Pickup Banner */}
        {scheduleData?.pickupItems && scheduleData.pickupItems.length > 0 && (
          <PickupBanner
            items={scheduleData.pickupItems}
            dateLabel={selectedDayInfo?.isToday ? 'Today' : selectedDayInfo?.label}
          />
        )}

        {/* No schedule for selected date */}
        {!isLoading && !error && !scheduleData?.schedule && (
          <Card variant="elevated">
            <CardContent>
              <div className="text-center py-8">
                <span className="text-4xl mb-4 block">📅</span>
                <h3 className="font-semibold text-forest-900 mb-2">
                  {selectedDayInfo?.isToday ? 'No Schedule Today' : `No Schedule for ${selectedDayInfo?.label}`}
                </h3>
                <p className="text-sage-600">
                  {selectedDayInfo?.isToday
                    ? "You don't have any routes assigned for today."
                    : `You don't have any routes assigned for ${selectedDayInfo?.label?.toLowerCase()}.`
                  }
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Schedule */}
        {!isLoading && stops.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-semibold text-forest-900">
                {selectedDayInfo?.isToday ? "Today's Schedule" : `${selectedDayInfo?.label}'s Schedule`}
              </h2>
              <span className="text-sm text-sage-500">
                {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                  timeZone: 'America/Los_Angeles',
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </div>

            <div className="space-y-3">
              {stops.map((stop, index) => (
                <Card
                  key={stop.id}
                  variant={loggedClients.has(stop.id) ? 'elevated' : 'default'}
                  hover
                  className={`animate-slide-up ${loggedClients.has(stop.id) ? 'ring-2 ring-primary-500' : ''}`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex gap-4">
                    {/* Stop number column */}
                    <div className="flex flex-col items-center">
                      <div className="w-8 h-8 rounded-full bg-forest-100 flex items-center justify-center">
                        <span className="text-sm font-semibold text-forest-700">
                          {stop.stopOrder}
                        </span>
                      </div>
                      {index < stops.length - 1 && (
                        <div className="flex-1 w-px bg-sage-200 my-2" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className="font-semibold text-forest-900">{stop.clientName}</h3>
                          <p className="text-sm text-sage-600 truncate">{stop.address}</p>
                        </div>
                        {loggedClients.has(stop.id) ? (
                          <Badge variant="success">Logged</Badge>
                        ) : (
                          <ServiceLogButton
                            clientId={stop.id}
                            clientName={stop.clientName}
                            onLogComplete={() => handleLogComplete(stop.id)}
                          />
                        )}
                      </div>

                      {/* Services */}
                      {stop.services.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {stop.services.map((service) => (
                            <Badge key={service} variant="default" size="sm">
                              {service}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* WO Indicators */}
                      {(stop.openWorkorderCount > 0 || stop.pickupCount > 0) && (
                        <div className="mt-2 p-2 bg-sage-50 rounded-lg border border-sage-200">
                          <div className="flex flex-wrap gap-2 text-xs">
                            {stop.openWorkorderCount > 0 && (
                              <span className={`flex items-center gap-1 ${stop.hasUrgentWorkorder ? 'text-earth-700 font-medium' : 'text-sage-600'}`}>
                                {stop.hasUrgentWorkorder && <span className="text-earth-500">⚠️</span>}
                                📦 {stop.openWorkorderCount} Open WO{stop.openWorkorderCount > 1 ? 's' : ''}
                              </span>
                            )}
                            {stop.pickupCount > 0 && (
                              <span className="flex items-center gap-1 text-primary-700">
                                🚚 {stop.pickupCount} Pickup{stop.pickupCount > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {stop.notes && (
                        <p className="text-sm text-sage-500 mt-2 italic">
                          📝 {stop.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Route info if available */}
        {scheduleData?.route && (
          <section>
            <h2 className="font-display text-xl font-semibold text-forest-900 mb-4">
              Route Details
            </h2>
            <Card>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-sage-600">Route Code</p>
                    <p className="font-medium text-forest-900">{scheduleData.route.routeCode}</p>
                  </div>
                  <div>
                    <p className="text-sm text-sage-600">Route Name</p>
                    <p className="font-medium text-forest-900">
                      {scheduleData.route.routeName || '-'}
                    </p>
                  </div>
                  {scheduleData.route.notes && (
                    <div className="col-span-2">
                      <p className="text-sm text-sage-600">Notes</p>
                      <p className="text-forest-900">{scheduleData.route.notes}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </section>
        )}
      </main>

      {/* Bottom Navigation provided by AppShell MobileShell */}
      {/* Spacer for AppShell bottom nav */}
      <div className="h-20" />

      {/* Morning clock-in prompt (overlay - renders conditionally) */}
      <MorningClockPrompt employeeName={firstName} />
    </div>
  );
}

function formatAddress(client: Client): string {
  if (!client.address) return 'No address';
  const parts = [
    client.address.address1,
    client.address.city,
    client.address.state,
  ].filter(Boolean);
  return parts.join(', ') || 'No address';
}

// =============================================================================
// Scheduler Notes Banner
// =============================================================================

function SchedulerNotesBanner({ notes }: { notes: string }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="bg-amber-50 border-l-4 border-l-amber-500 rounded-r-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-amber-100/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-amber-600">📋</span>
          <span className="font-semibold text-amber-800 text-sm uppercase tracking-wide">
            Scheduler Note
          </span>
        </div>
        <span className="text-amber-600 text-sm">
          {expanded ? 'Hide' : 'Show'}
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-3">
          <p className="text-amber-900 text-sm">{notes}</p>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Ready for Pickup Banner
// =============================================================================

function PickupBanner({ items, dateLabel = 'Today' }: { items: PickupItem[]; dateLabel?: string }) {
  const [expanded, setExpanded] = useState(true);

  // Group items by client
  const groupedByClient = items.reduce((acc, item) => {
    const clientName = item.clientName || 'Unknown Client';
    if (!acc[clientName]) {
      acc[clientName] = [];
    }
    acc[clientName].push(item);
    return acc;
  }, {} as Record<string, PickupItem[]>);

  const clientCount = Object.keys(groupedByClient).length;

  return (
    <div className="bg-primary-50 border-l-4 border-l-primary-500 rounded-r-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-primary-100/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-primary-600">🚚</span>
          <span className="font-semibold text-primary-800 text-sm uppercase tracking-wide">
            Pickup {dateLabel}
          </span>
          <Badge variant="success" size="sm">
            {items.length} item{items.length !== 1 ? 's' : ''}
          </Badge>
        </div>
        <span className="text-primary-600 text-sm">
          {expanded ? 'Collapse' : 'Expand'}
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-3 space-y-3">
          {Object.entries(groupedByClient).map(([clientName, clientItems]) => (
            <div key={clientName}>
              <p className="text-xs font-medium text-primary-700 mb-1">
                @ {clientName} ({clientItems.length} item{clientItems.length !== 1 ? 's' : ''})
              </p>
              <div className="space-y-1 pl-3">
                {clientItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 text-sm text-primary-900">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                    <span>{item.description}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {clientCount > 1 && (
            <p className="text-xs text-primary-600 pt-1 border-t border-primary-200">
              {clientCount} client{clientCount !== 1 ? 's' : ''} with pickups
            </p>
          )}
        </div>
      )}
    </div>
  );
}

